import React, { useState, useEffect } from 'react';
import {
  Button,
  Link,
  makeStyles,
  tokens,
  shorthands
} from '@fluentui/react-components';
import { MessageBar, MessageBarBody, MessageBarTitle, MessageBarActions } from './Common/ScrollableMessageBar';
import { Warning20Regular, CheckmarkCircle20Regular, DismissCircle20Regular } from '@fluentui/react-icons';
import { apiClient } from '../services/apiClient';
import { useMSALAuth } from '../contexts/MSALAuthContext';

const useStyles = makeStyles({
  container: {
    position: 'fixed',
    top: '60px',
    left: 0,
    right: 0,
    zIndex: 1000,
    ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalL),
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow4
  },
  messageBar: {
    maxWidth: '1200px',
    ...shorthands.margin('0', 'auto')
  }
});

interface PendingDomain {
  domain: string;
  organization: string;
  detected_at: string;
}

export const DomainApprovalBanner: React.FC = () => {
  const styles = useStyles();
  const { isAuthenticated, isAuthReady, user } = useMSALAuth();
  const [pendingDomains, setPendingDomains] = useState<PendingDomain[]>([]);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Only make API calls if authenticated and ready
    if (isAuthenticated && isAuthReady && user) {
      checkPendingApprovals();
      // Check every 5 minutes
      const interval = setInterval(checkPendingApprovals, 5 * 60 * 1000);
      return () => clearInterval(interval);
    } else {
      setLoading(false);
    }
  }, [isAuthenticated, isAuthReady, user]);

  const checkPendingApprovals = async () => {
    // Double check authentication before making API call
    if (!isAuthenticated || !isAuthReady || !user) {
      setLoading(false);
      return;
    }

    try {
      const response = await apiClient.get<any>('/domains/pending-approvals');
      const data: any = response.data || response;
      
      if (data.success && data.data?.pendingDomains) {
        setPendingDomains(data.data.pendingDomains);
      }
    } catch (error) {
      console.error('Failed to check pending approvals:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (domains: string[]) => {
    try {
      const response = await apiClient.post<any>('/domains/approve', {
        domains,
        action: 'approve',
        approvedBy: 'admin'
      });

      const data: any = response.data || response;
      if (data.success) {
        // Refresh the list
        checkPendingApprovals();
      }
    } catch (error) {
      console.error('Failed to approve domains:', error);
    }
  };

  const handleReject = async (domains: string[]) => {
    try {
      const response = await apiClient.post<any>('/domains/approve', {
        domains,
        action: 'reject',
        rejectionReason: 'Not authorized for Teams federation'
      });

      const data: any = response.data || response;
      if (data.success) {
        // Refresh the list
        checkPendingApprovals();
      }
    } catch (error) {
      console.error('Failed to reject domains:', error);
    }
  };

  if (loading || dismissed || pendingDomains.length === 0) {
    return null;
  }

  return (
    <div className={styles.container} data-testid="domain-approval-banner">
      <MessageBar 
        className={styles.messageBar}
        intent="warning"
        icon={<Warning20Regular />}
        data-testid="domain-approval-message-bar"
      >
        <MessageBarBody data-testid="domain-approval-message-body">
          <MessageBarTitle data-testid="domain-approval-message-title">Domain Approval Required</MessageBarTitle>
          {pendingDomains.length === 1 ? (
            <>
              New domain <strong data-testid="domain-approval-banner-domain-name">{pendingDomains[0].domain}</strong> from {pendingDomains[0].organization} requires approval
            </>
          ) : (
            <>
              {pendingDomains.length} new domains require approval
            </>
          )}
        </MessageBarBody>
        <MessageBarActions
          data-testid="domain-approval-message-actions"
          containerAction={
            <Button 
              aria-label="dismiss" 
              appearance="transparent" 
              icon={<DismissCircle20Regular />}
              onClick={() => setDismissed(true)}
              data-testid="domain-approval-dismiss"
            />
          }
        >
          <Button 
            appearance="primary"
            icon={<CheckmarkCircle20Regular />}
            onClick={() => handleApprove(pendingDomains.map(d => d.domain))}
            data-testid="domain-approval-approve-all"
          >
            Approve All
          </Button>
          <Link href="/domains" appearance="subtle" data-testid="domain-approval-review-link">
            Review
          </Link>
        </MessageBarActions>
      </MessageBar>
    </div>
  );
};